import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import useOffers from 'api/useOffers';
import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { OfferDesignSelector } from 'views/Offers/components/OfferDesignSelector';
import { SegmentsSection } from 'views/Offers/components/sections';
import { RollingOfferFormFields } from 'views/Offers/RollingOffers/Form/RollingOfferForm.consts';
import { ISettingsTabProps } from 'views/Offers/RollingOffers/Form/RollingOfferForm.types';

import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import { Separator } from 'components/ui/separator';

const SettingsTab: React.FC<ISettingsTabProps> = ({
  rollingOfferId,
  edit,
  chosenSegment,
  setChosenSegment,
  offerDesignOptions,
  data,
  dup
}): React.ReactElement => {
  const { watch, setValue, formState } = useFormContext<RollingOfferFormFields>();
  const { errors, touchedFields } = formState;

  const values = watch();

  const [isNameExists, setIsNameExists] = useState<boolean>(false);

  const { getOffers } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });

  const isNameError = (touchedFields.name && !!errors.name) || isNameExists;
  const nameErrorMessage = useMemo((): string => {
    if (touchedFields.name && errors.name?.message) {
      return errors.name.message;
    }
    if (isNameExists) {
      return 'Name already exists';
    }
    return '';
  }, [touchedFields.name, errors.name?.message, isNameExists]);

  const isExternalIdError =
    !!errors.publisherOfferId && (touchedFields.publisherOfferId || dup);
  const externalIdErrorMessage = useMemo((): string => {
    if (touchedFields.publisherOfferId || dup) {
      return errors.publisherOfferId?.message || '';
    }
    return '';
  }, [touchedFields.publisherOfferId, dup, errors.publisherOfferId?.message]);

  const offerDesignErrorMessage = useMemo((): string => {
    if (touchedFields.offerUiId && errors.offerUiId?.message) {
      return errors.offerUiId.message;
    }
    return '';
  }, [touchedFields.offerUiId, errors.offerUiId?.message]);

  useEffect(() => {
    const offerExists = getOffers.data?.offers.some(
      (offer: Offer) => offer.name === values.name
    );
    setIsNameExists(!!offerExists && !edit);
  }, [values.name, edit, getOffers.data]);

  useEffect(() => {
    if (data?.segments) {
      setChosenSegment(data.segments);
    }
  }, [data?.segments, setChosenSegment]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setValue('name', e.target.value, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const handleExternalIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setValue('publisherOfferId', e.target.value, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const handleOfferDesignChange = useCallback(
    (value: string): void => {
      setValue('offerUiId', value, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const handlePriorityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setValue('priority', Number(e.target.value), { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const handleSegmentsChange = useCallback(
    (newSegments: string[]): void => {
      setChosenSegment(newSegments);
      setValue('segments', newSegments, { shouldDirty: true });
    },
    [setChosenSegment, setValue]
  );

  return (
    <div className="flex flex-col">
      <RichText
        title="General"
        description="Complete the required fields for your offer."
        className="mb-5"
      />
      <div className="flex flex-col gap-9">
        <div className="grid grid-cols-2 gap-5">
          <Input
            id="rolling-offer-name"
            label="Name"
            name="name"
            value={values.name}
            onChange={handleNameChange}
            isRequired
            placeholder="Name"
            state={isNameError ? 'error' : 'default'}
            description={nameErrorMessage}
            dataTestId="rollingOfferNameInput"
          />
          <Input
            id="rolling-offer-external-id"
            label="External ID"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleExternalIdChange}
            isRequired
            placeholder="External ID"
            state={isExternalIdError ? 'error' : 'default'}
            description={externalIdErrorMessage}
            dataTestId="rollingOfferIdInput"
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <OfferDesignSelector
            options={offerDesignOptions}
            selectedValue={values.offerUiId}
            onChange={handleOfferDesignChange}
            listDataTestId="dashRollingOfferDesignList"
            buttonDataTestId="rollingOfferDesignBox"
            placeholder="Select"
            description={offerDesignErrorMessage}
          />
          <Input
            id="rolling-offer-priority"
            label="Priority"
            name="priority"
            value={values.priority}
            onChange={handlePriorityChange}
            isRequired
            type="number"
            placeholder="1"
            min={1}
            max={10}
            tooltipIcon="fa-circle-info"
            infoTooltipContent="Priority decides which rolling offer show, based on their importance. The lower the number, the higher the priority."
          />
        </div>
      </div>
      <Separator className="my-9 bg-base-border" />
      <SegmentsSection
        items={chosenSegment}
        onItemsChange={handleSegmentsChange}
        description="Create or add players segments to your offer."
        placeholder="Search or create new"
        inputTestId="rollingOfferSegmentsInput"
        listTestId="rollingOfferSegmentsList"
      />
    </div>
  );
};

export default SettingsTab;
